import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import ImageLightbox from './ImageLightbox';
import '../styles/ComicPanel.css';

const ComicPanel = ({
  id,
  type = 'dialogue', // 'splash', 'action', 'reaction', 'dialogue', 'establishing', 'player'
  imageUrl,
  isGeneratingImage = false,
  narrator = '',
  playerAction = '',
  mood = 'mystery',
  onImageLoad,
  onImageError,
  isLatest = false
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  // Open lightbox when image is tapped (only if loaded)
  const handleImageClick = () => {
    if (imageLoaded && imageUrl && !isGeneratingImage) {
      setShowLightbox(true);
    }
  };

  // Animate in when image loads or after a delay for player panels
  useEffect(() => {
    if (type === 'player') {
      // Player panels show immediately
      setShowContent(true);
    } else if (isGeneratingImage) {
      // Show content immediately while generating (text should be visible)
      setShowContent(true);
    } else if (imageLoaded || imageError || !imageUrl) {
      // Slight delay for dramatic effect
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    }
  }, [imageLoaded, imageError, imageUrl, type, isGeneratingImage]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    if (onImageLoad) onImageLoad(id);
  };

  const handleImageError = () => {
    setImageError(true);
    if (onImageError) onImageError(id);
  };

  // Panel size classes based on type
  const getPanelSizeClass = () => {
    switch (type) {
      case 'splash':
        return 'panel-splash';
      case 'establishing':
        return 'panel-large';
      case 'action':
        return 'panel-action';
      case 'reaction':
        return 'panel-medium';
      default:
        return 'panel-standard';
    }
  };

  // Mood-based accent color
  const getMoodClass = () => {
    switch (mood) {
      case 'danger':
        return 'mood-danger';
      case 'hope':
        return 'mood-hope';
      case 'tension':
        return 'mood-tension';
      case 'power':
        return 'mood-power';
      default:
        return 'mood-mystery';
    }
  };

  return (
    <div 
      className={`comic-panel ${getPanelSizeClass()} ${getMoodClass()} ${showContent ? 'visible' : 'hidden'} ${isLatest ? 'latest' : ''}`}
      data-panel-id={id}
    >
      {/* Player action shown at TOP of panel (if this panel has one) */}
      {playerAction && (
        <div className="player-action-container">
          <div className="player-action-box">
            <div className="action-label">YOUR ACTION</div>
            <p className="action-text">{playerAction}</p>
          </div>
        </div>
      )}

      {/* Panel border with noir styling */}
      <div className="panel-border">
        <div className="panel-inner">
          
          {/* Image area */}
          <div 
            className={`panel-image-container ${imageLoaded ? 'tappable' : ''}`}
            onClick={handleImageClick}
            role={imageLoaded ? 'button' : undefined}
            tabIndex={imageLoaded ? 0 : undefined}
            aria-label={imageLoaded ? 'Tap to zoom image' : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleImageClick();
              }
            }}
          >
            {/* Loading state */}
            {isGeneratingImage && (
              <div className="image-generating">
                <div className="sketch-animation">
                  <div className="sketch-line sketch-line-1"></div>
                  <div className="sketch-line sketch-line-2"></div>
                  <div className="sketch-line sketch-line-3"></div>
                  <div className="sketch-line sketch-line-4"></div>
                </div>
                <div className="generating-text">
                  <span className="generating-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                  RENDERING SCENE
                  <span className="generating-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                </div>
              </div>
            )}

            {/* Error state - show stylized placeholder */}
            {imageError && !isGeneratingImage && (
              <div className="image-error">
                <div className="error-pattern"></div>
                <div className="error-text">TEMPORAL INTERFERENCE</div>
              </div>
            )}

            {/* Actual image */}
            {imageUrl && !isGeneratingImage && (
              <img
                src={imageUrl}
                alt="Comic panel scene"
                className={`panel-image ${imageLoaded ? 'loaded' : 'loading'}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}

            {/* Halftone overlay for comic effect */}
            <div className="halftone-overlay"></div>
            
            {/* Vignette effect */}
            <div className="vignette-overlay"></div>
          </div>

          {/* Panel number (optional decorative element) */}
          <div className="panel-number" aria-hidden="true"></div>
        </div>
      </div>

      {/* Story text BELOW the image - only show after image is ready */}
      {narrator && !isGeneratingImage && (
        <div className="story-text-container">
          <div className="story-text-box">
            <div className="story-text">
              <ReactMarkdown>{narrator}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* Mood accent glow */}
      <div className="mood-glow"></div>

      {/* Image lightbox for zooming */}
      {showLightbox && (
        <ImageLightbox
          imageUrl={imageUrl}
          alt="Comic panel scene - zoomed view"
          onClose={() => setShowLightbox(false)}
        />
      )}
    </div>
  );
};

export default ComicPanel;